import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import api from '../../services/api.ts';
import FormPage from '../../components/ui/FormPage.tsx';
import { parseOptionalNumber } from '../../utils/numberGuards.ts';

const KM_INVALID_MSG = 'Informe uma quilometragem válida (número ≥ 0).';

const FrotaRegistrarKm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [km, setKm] = useState('');
  const [loggedAt, setLoggedAt] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');

  const { data: vehicle, isLoading } = useQuery(
    ['fleet-vehicle', id],
    async () => {
      const res = await api.get(`/patrimonio/frota/${id}`);
      return res.data;
    },
    { enabled: !!id }
  );

  const minKm = Number(vehicle?.currentKm) || 0;

  const mutation = useMutation(
    async () => {
      const parsedKm = parseOptionalNumber(km);
      const res = await api.post(`/patrimonio/frota/${id}/km`, {
        km: parsedKm,
        loggedAt,
        notes: notes || undefined,
      });
      return res.data;
    },
    {
      onSuccess: () => navigate(`/patrimonio/frota/${id}`),
      onError: (err: any) => setError(err?.response?.data?.error || 'Erro ao registrar km'),
    }
  );

  const validateKm = (raw: string): string | null => {
    if (raw === '' || raw === null || raw === undefined) {
      return KM_INVALID_MSG;
    }
    const n = parseOptionalNumber(raw);
    if (n === null || Number.isNaN(n) || n < 0) {
      return KM_INVALID_MSG;
    }
    if (n < minKm) {
      return `Informe um valor ≥ ${minKm.toLocaleString('pt-BR')} km.`;
    }
    return null;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = validateKm(km);
    if (msg) {
      setFieldError(msg);
      return;
    }
    setFieldError('');
    setError('');
    mutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <FormPage
      backTo={`/patrimonio/frota/${id}`}
      backLabel="Voltar ao detalhe do veículo"
      breadcrumb="Patrimônio / Frota / Registrar km"
      title="Registrar km"
      subtitle={`${vehicle?.make || ''} ${vehicle?.model || ''} · ${vehicle?.licensePlate || ''}`}
      hint={`Última leitura: ${minKm.toLocaleString('pt-BR')} km. Informe a nova quilometragem (número ≥ 0).`}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(`/patrimonio/frota/${id}`)}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="frota-km-form"
            disabled={mutation.isLoading}
            className="btn btn-primary"
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar quilometragem'}
          </button>
        </>
      }
    >
      {error && (
        <div className="field-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}
      <form id="frota-km-form" noValidate onSubmit={onSubmit}>
        <div className="form-section-title">Nova leitura</div>
        <div className="form-grid">
          <label className="field">
            <span>Quilometragem atual (km)</span>
            <input
              className={`input${fieldError ? ' error' : ''}`}
              type="text"
              inputMode="numeric"
              value={km}
              onChange={e => {
                setKm(e.target.value);
                if (fieldError) setFieldError('');
              }}
              placeholder={String(minKm)}
              aria-invalid={!!fieldError}
            />
            {fieldError && <div className="field-error">{fieldError}</div>}
          </label>
          <label className="field">
            <span>Data da leitura</span>
            <input
              className="input"
              type="date"
              required
              value={loggedAt}
              onChange={e => setLoggedAt(e.target.value)}
            />
          </label>
          <label className="field full">
            <span>Observação (opcional)</span>
            <input
              className="input"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Ex.: após viagem"
            />
          </label>
        </div>
      </form>
    </FormPage>
  );
};

export default FrotaRegistrarKm;
